"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTapeBuilderFromArtifactsDocument = exports.createTapeBuilder = void 0;
const common_1 = require("@finley-technologies/common");
const tape_sanitizer_1 = require("@finley-technologies/tape-sanitizer");
const convertor_1 = require("./convertor");
const dbNodes = __importStar(require("./dbNodes/dbNodes"));
const typeguards = __importStar(require("./typeguards"));
const operationConvertors_1 = require("./uiNodes/operationConvertors");
/**
 * create tape builder. Tape Builder provides interface to generate tapes and their definitions
 * given TapeBuilderTask and dependent tapes
 * @param {DbTable.ArtifactWithSlugs} artifacts - array of artifacts from the document.
 *  All the tape dependencies must be present in this map (except for raw tapes)
 * @returns {ITapeBuilder} tape builder interface
 */
const createTapeBuilder = (artifacts) => {
    // returns the artifact record for the given tape id
    const getTask = (id) => {
        const artifact = artifacts.find((record) => record.id === id);
        (0, common_1.finleyAssert)(artifact, `could not find tape with [artifactId:${id}]`);
        (0, common_1.finleyAssert)(typeguards.isTapeBuilderTask(artifact.configuration), `createTapeBuilder:tape configuration is not of task TapeBuilderTask: ${JSON.stringify(artifact.configuration)}`);
        return artifact.configuration;
    };
    const getIdFromSlug = (slug) => {
        const artifact = artifacts.find(({ artifactSlug }) => slug === artifactSlug);
        (0, common_1.finleyAssert)(artifact, `createTapeBuilder:could not find artifact with [artifactSlug:${slug}]`);
        return artifact.id;
    };
    const getSlugFromId = (id) => {
        const artifact = artifacts.find(({ id: artifactId }) => id === artifactId);
        (0, common_1.finleyAssert)(artifact, `createTapeBuilder:could not find artifact with [artifactId:${id}]`);
        return artifact.artifactSlug;
    };
    // `Transformation` is configuration for how tapes are 'sanitized' or 'edited'
    // or 'joined'; it includes (a) input tape definitions and (b) operation-
    // specific parameters. Tape builder has (b) in its TapeBuilderTask; it
    // generates (a) by walking back the dependency tree via fieldDefinitionCallback.
    // The per-operation mapping now lives in each operation's convertor
    // (`toTransformation`); this delegates to it (see getOperationTransformation).
    const convertTransformationOperation = ({ task, fieldDefinitionCallback, }) => (0, operationConvertors_1.getOperationTransformation)(task, fieldDefinitionCallback);
    // generates tape definition (base fields) for the given artifact
    const generateTapeDefinition = (artifactId) => {
        const task = getTask(artifactId);
        // generateTapeDefinition and convertTransformationOperation
        // are mutually recursive functions, hence the callback
        const transformation = convertTransformationOperation({
            task,
            fieldDefinitionCallback: (tapeSlug) => generateTapeDefinition(getIdFromSlug(tapeSlug)),
        });
        if (!transformation.isValid) {
            return {
                isValid: false,
                errors: transformation.errors,
            };
        }
        if (!transformation.data) {
            // for tapes which has no definition associated.
            // e.g. xlsx2csv,
            return {
                isValid: true,
                data: null,
            };
        }
        const output = (0, tape_sanitizer_1.transformDataTapeDefinition)(transformation.data);
        if (output.isValid) {
            return {
                isValid: true,
                data: output.data,
            };
        }
        // decorate errors with the artifactSlug.
        const errors = output.errors.map((error) => `[artifactSlug:${getSlugFromId(artifactId)}]:${error.message}`);
        return {
            isValid: false,
            errors,
        };
    };
    const getTransformation = (artifactId) => {
        const task = getTask(artifactId);
        // convert configuration to tape-sanitizer transformation
        const transformation = convertTransformationOperation({
            task,
            fieldDefinitionCallback: (tapeSlug) => generateTapeDefinition(getIdFromSlug(tapeSlug)),
        });
        // we do not expect errors in transformation at the time
        // of generating tape, as these errors will be surfaced before
        // saving tape generation pipeline
        (0, common_1.finleyAssert)(transformation.isValid, `transformation is invalid: ${JSON.stringify(transformation)}`);
        return {
            transformation: transformation.data,
            srcArtifactSlug: task.srcArtifactSlug,
        };
    };
    const generateTape = async ({ artifactId, inputTapeBuffers, inputBufferExtensions, pdfExtractor, assetExtractor, pyXlsx2Csv2Extractor, pyXlsxVirtualSheet2CsvExtractor, pyCsvVirtualSheetExtractor, ai2CsvExtractor, csvParser, }) => {
        const task = getTask(artifactId);
        const getBufferExtension = (slug) => {
            const inputArtifactId = getIdFromSlug(slug);
            const extension = inputBufferExtensions?.get(inputArtifactId);
            return extension &&
                common_1.borrowingBasePipeline.isSupportedFileExtension(extension)
                ? extension
                : undefined;
        };
        const getBuffer = async (slug) => {
            const inputArtifactId = getIdFromSlug(slug);
            const buffer = inputTapeBuffers.get(inputArtifactId);
            // if the input buffer does not exist see if it can be created.
            // as long as leaf nodes needed for the target node are
            // provided we should be able to generate requested artifactId.
            if (!buffer) {
                const previousResult = await generateTape({
                    artifactId: inputArtifactId,
                    inputTapeBuffers,
                    inputBufferExtensions,
                    pdfExtractor,
                    assetExtractor,
                    pyXlsx2Csv2Extractor,
                    pyXlsxVirtualSheet2CsvExtractor,
                    pyCsvVirtualSheetExtractor,
                    ai2CsvExtractor,
                    csvParser,
                });
                return previousResult;
            }
            (0, common_1.finleyAssert)(buffer, `could not find/generate buffer for [artifactSlug:${slug}]`);
            return {
                isValid: true,
                data: {
                    outputBuffer: buffer,
                },
                supplementals: [],
            };
        };
        // operation-specific input slugs (besides the implicit srcArtifactSlug) are
        // owned by each operation's convertor; see getOperationInputSlugs.
        const inputSlugs = (0, operationConvertors_1.getOperationInputSlugs)(task);
        const buffersWithExtensions = [];
        // collect buffers for all the inputs
        for (const inputSlug of inputSlugs) {
            // eslint-disable-next-line no-await-in-loop
            const dataTapeOutput = await getBuffer(inputSlug);
            if (dataTapeOutput.isValid) {
                buffersWithExtensions.push({
                    buffer: dataTapeOutput.data.outputBuffer,
                    extension: getBufferExtension(inputSlug),
                });
            }
            else {
                // return on any failure to get the buffer.
                return dataTapeOutput;
            }
        }
        const output = await (0, tape_sanitizer_1.transformDataTape)({
            transformation: getTransformation(artifactId).transformation,
            buffersWithExtensions,
            pdfExtractor,
            assetExtractor,
            pyXlsx2Csv2Extractor,
            pyXlsxVirtualSheet2CsvExtractor,
            pyCsvVirtualSheetExtractor,
            ai2CsvExtractor,
            csvParser,
        });
        return output;
    };
    const getGeneratedLoanTapeConfig = (artifactId) => {
        // we assume that the artifact is a generatedLoanTape
        const artifact = artifacts.find(({ id }) => id === artifactId);
        (0, common_1.finleyAssert)(typeguards.isTapeBuilderRecordUI(artifact), 'not a tapeBuilder record');
        (0, common_1.finleyAssert)(dbNodes.generatedLoanTape.typeGuard(artifact), 'not a loan tape');
        return artifact.configuration;
    };
    const generateTapeDefinitionBySlug = (artifactSlug) => {
        return generateTapeDefinition(getIdFromSlug(artifactSlug));
    };
    return {
        generateTapeDefinition,
        generateTapeDefinitionBySlug,
        generateTape,
        getGeneratedLoanTapeConfig,
        getTransformation,
    };
};
exports.createTapeBuilder = createTapeBuilder;
const createTapeBuilderFromArtifactsDocument = (artifactsDocument) => {
    const { dataTapes } = convertor_1.convertor.dbRecordsFromArtifactsDocument({
        artifactsDocument,
    });
    return (0, exports.createTapeBuilder)(dataTapes);
};
exports.createTapeBuilderFromArtifactsDocument = createTapeBuilderFromArtifactsDocument;
//# sourceMappingURL=tapeBuilder.js.map